import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MsalRedirectHandlerService {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router
  ) {}

  initialize(): void {
    this.initializeRedirectHandler();
    this.initializeEventSubscriptions();
  }

  private initializeRedirectHandler(): void {
    // Handle redirect promise on app initialization
    this.msalService.instance.handleRedirectPromise().then((result) => {
      if (result) {
        console.log('Successfully handled redirect promise', result);
        this.setSsoCookie(result.idToken);
        this.handleSuccessfulLogin();
      }
    }).catch((error) => {
      console.error('Error handling redirect promise:', error);
    });
  }

  private initializeEventSubscriptions(): void {
    // Wait until interaction is none before setting active account
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setActiveAccount();
      });

    // Listen for MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: any) => {
        if (result.eventType === EventType.LOGIN_SUCCESS || result.eventType === EventType.SSO_SILENT_SUCCESS) {
          console.log('Login or silent success event:', result);
          if (result.payload && result.payload.idToken) {
            this.setSsoCookie(result.payload.idToken);
          }
          this.handleSuccessfulLogin();
        } else if (result.eventType === EventType.LOGIN_FAILURE) {
          console.error('Login failure event:', result);
          this.handleLoginFailure();
        } else if (result.eventType === EventType.LOGOUT_SUCCESS) {
          console.log('Logout success event:', result);
          this.clearSsoCookie();
          this.handleLogoutSuccess();
        }
      });
  }

  private setSsoCookie(idToken: string): void {
    const cookieName = environment.ssoCookieName;
    const domain = environment.ssoCookieDomain;
    
    // Configurar la cookie transversal usando variables de entorno
    document.cookie = `${cookieName}=${idToken}; domain=${domain}; path=/; max-age=3600; Secure; SameSite=Lax`;
    // Fallback sin dominio explícito
    document.cookie = `${cookieName}=${idToken}; path=/; max-age=3600; Secure; SameSite=Lax`;
    
    console.log(`Cookie SSO transversal configurada: ${cookieName} en ${domain}`);
  }

  private clearSsoCookie(): void {
    const cookieName = environment.ssoCookieName;
    const domain = environment.ssoCookieDomain;
    
    // Clear con dominio
    document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
    // Clear sin dominio
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
  }

  private setActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      return;
    }

    if (accounts.length > 1) {
      // Multiple accounts, set the first one as active
      this.msalService.instance.setActiveAccount(accounts[0]);
    } else {
      // Single account
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private handleSuccessfulLogin(): void {
    // Navigate to intended route or home
    const intendedRoute = sessionStorage.getItem('msal.intended.route');
    
    // Priority 1: Intended route from session storage (manual login flow)
    if (intendedRoute) {
      sessionStorage.removeItem('msal.intended.route');
      console.log('Navigating to intended route:', intendedRoute);
      this.router.navigateByUrl(intendedRoute);
      return;
    }

    // Priority 2: Stay where we are if we're already on an admin or private page
    // This is crucial for page reloads
    const currentPath = window.location.pathname;
    if (currentPath.includes('/admin/') && !currentPath.includes('login')) {
      console.log('Already on admin route during login success, staying put:', currentPath);
      return;
    }

    // Priority 3: Default fallback for generic login
    if (currentPath.includes('login') || currentPath === '/' || currentPath === '') {
      console.log('Default login redirect to dashboard');
      this.router.navigate(['/admin/dashboard']);
    }
  }

  private handleLoginFailure(): void {
    this.router.navigate(['/login-failed']);
  }

  private handleLogoutSuccess(): void {
    this.router.navigate(['/']);
  }

  // Store intended route before login
  storeIntendedRoute(route: string): void {
    sessionStorage.setItem('msal.intended.route', route);
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}